import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { humanizeEnums } from '@/features/agentic-insights/components/data/humanizeEnums';
import type { PersonaView } from '@/features/agentic-insights/components/data/resolvePersonaView';
import type { Alternative, Finding } from '@/features/agentic-insights/components/data/types';
import { buildFigureLine } from '@/features/agentic-insights/components/experimental/shared/data/figureLine';
import { ReferenceLead } from '@/features/agentic-insights/components/experimental/shared/prose/ReferenceLead';
import { RunProse } from '@/features/agentic-insights/components/experimental/shared/prose/RunProse';
import { SectionLabel } from '@/features/agentic-insights/components/experimental/shared/ui/controls';
import { Field } from '@/features/agentic-insights/components/experimental/shared/ui/Field';
import type { Footnote } from '@/features/agentic-insights/components/experimental/shared/ui/Footnotes';
import { Footnotes } from '@/features/agentic-insights/components/experimental/shared/ui/Footnotes';
import type { LabelledRow } from '@/features/agentic-insights/components/experimental/shared/ui/LabelledRows';
import { LabelledRows } from '@/features/agentic-insights/components/experimental/shared/ui/LabelledRows';
import { MEASURE, MEASURE_WIDE, RECOMMENDATION_SURFACE, TONE, TYPE } from '../language';
import { ActionParameters } from './ActionParameters';

interface FindingArgumentProps {
  finding: Finding;
  view: Extract<PersonaView, { kind: 'voiced' }>;
}

const VERDICT_TONE: Record<Alternative['verdict'], string> = {
  eliminated: TONE.eliminated,
  notEliminated: TONE.open,
  untestable: TONE.untestable,
};

/**
 * One finding in full: the figure it turns on, what it comes down to, and what to do about it.
 *
 * Only those three are open. What it rests on, what else could explain it and where the figures came
 * from are all real and all long — open, they made a finding several screens tall and buried the
 * action a reader came for — so they sit at the foot as footnotes, one line each with its count,
 * until asked for.
 *
 * Adds rather than restates: the claim and its standing are the reader's way in — the row they
 * opened in the list, or `FindingHead` in the Details reading — so this begins at the first thing
 * they do not already have in front of them.
 */
export const FindingArgument = ({ finding, view }: FindingArgumentProps) => {
  const { t } = useTranslation();

  const figure = buildFigureLine(finding, (denominator) =>
    t(($) => $.loopAgenticInsights.finding.denominator, { denominator }),
  );

  const alternatives = finding.evidence.alternatives ?? [];
  const sources = finding.evidence.sources ?? [];

  const reasoningRows: LabelledRow[] = finding.description.map((block, index) => ({
    key: `${block.lead}-${index}`,
    label: (
      <Field name="description[].lead">
        <ReferenceLead>{block.lead}</ReferenceLead>
      </Field>
    ),
    value: (
      <Field name="description[].text">
        <RunProse references="chip">{block.text}</RunProse>
      </Field>
    ),
    note: block.note ? <RunProse references="chip">{block.note}</RunProse> : undefined,
  }));

  const sourceRows: LabelledRow[] = sources.map((source) => ({
    key: source.label,
    label: <Field name="evidence.sources[].label">{source.label}</Field>,
    value: (
      <Field name="evidence.sources[].statement">
        <RunProse references="chip">{source.statement}</RunProse>
      </Field>
    ),
  }));

  const footnotes: Footnote[] = [
    {
      id: 'rests-on',
      label: t(($) => $.loopAgenticInsights.experimental.argument.reasoning),
      count: reasoningRows.length,
      children: (
        <LabelledRows
          type={TYPE}
          measure={MEASURE}
          rows={reasoningRows}
          testId="agentic-x-reasoning"
        />
      ),
    },
    {
      id: 'alternatives',
      label: t(($) => $.loopAgenticInsights.experimental.audit.alternatives),
      count: alternatives.length,
      children: (
        <ol className="flex flex-col gap-5">
          {alternatives.map((alternative, index) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: alternatives from an immutable run, with no id of their own
              key={index}
              data-testid="agentic-x-alternative"
              className="grid grid-cols-[1rem_1fr] gap-x-3 gap-y-1"
            >
              <span className={`${TYPE.ordinal} text-right`}>{index + 1}</span>
              <p className={`${TYPE.detail} ${MEASURE}`}>
                <Field name="evidence.alternatives[].statement">
                  <RunProse references="chip">{alternative.statement}</RunProse>
                </Field>
              </p>
              <p className={`${TYPE.meta} col-start-2 flex flex-wrap gap-2`}>
                <span className={VERDICT_TONE[alternative.verdict]}>
                  <Field name="evidence.alternatives[].verdict">
                    {t(($) => $.loopAgenticInsights.alternatives.verdict[alternative.verdict])}
                  </Field>
                </span>
                {alternative.impact !== 'none' ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      {t(
                        ($) =>
                          $.loopAgenticInsights.alternatives.impact[
                            alternative.impact === 'invalidates' ? 'invalidates' : 'magnitude'
                          ],
                      )}
                    </span>
                  </>
                ) : null}
              </p>
              <p className={`${TYPE.body} ${MEASURE} col-start-2`}>
                <RunProse references="chip">{alternative.note}</RunProse>
              </p>
            </li>
          ))}
        </ol>
      ),
    },
    {
      id: 'sources',
      label: t(($) => $.loopAgenticInsights.experimental.audit.sources),
      count: sourceRows.length,
      children: (
        <LabelledRows type={TYPE} measure={MEASURE} rows={sourceRows} testId="agentic-x-sources" />
      ),
    },
  ];

  return (
    <div data-testid="agentic-x-argument" className="flex flex-col gap-8">
      {/* The figure the claim turns on, opening the expansion — but as a note, not as a headline.
          Set large it was bigger than the claim it is evidence for, and its four free-text fields
          described one figure as three competing ones. A step below the prose beneath it in size and
          colour: a reader who wants the number has it immediately, and a reader who wants the
          argument reads straight past it. */}
      <p
        data-testid="agentic-x-figure"
        className={`${TYPE.footnote} ${MEASURE_WIDE} flex flex-wrap items-baseline gap-x-2`}
      >
        {/* Mono and tabular so the digits still scan as a figure, but at the line's own size and
            colour: set in medium and primary it was the loudest thing in the expansion. */}
        <span className="font-mono tabular-nums">
          <Field name="metric">{figure.metric}</Field>
        </span>
        <Field name="metricLabel">{humanizeEnums(figure.label)}</Field>
        {figure.qualifiers.map((qualifier) => (
          <Fragment key={qualifier}>
            <span aria-hidden="true">·</span>
            <span>{qualifier}</span>
          </Fragment>
        ))}
      </p>

      <section className="flex flex-col gap-3">
        <SectionLabel
          type={TYPE}
          label={t(($) => $.loopAgenticInsights.experimental.argument.inShort)}
        />
        <p className={`${TYPE.detail} ${MEASURE}`}>
          <Field name="headline">
            <RunProse>{finding.headline}</RunProse>
          </Field>
        </p>
      </section>

      <section className="flex flex-col gap-4">
        <SectionLabel
          type={TYPE}
          label={t(($) => $.loopAgenticInsights.experimental.argument.whatToDo)}
          count={view.recommendations.length}
        />
        <ol className="flex flex-col gap-6">
          {view.recommendations.map((recommendation, index) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: positional identity is all a recommendation has in the contract
              key={index}
              data-testid="agentic-x-argument-action"
              // A real marker column rather than an arrow plus a guessed indent: the arrow's own
              // width is not the padding the body used, so the title sat a few pixels left of
              // everything beneath it. One grid, one left edge for the title and its body.
              //
              // An always-on surface rather than a hover wash: the grouping it does — this title
              // owns these parameters — is true whether or not the pointer is there. A lift, not a
              // shade: the page ground is `surface-foundation` and this is `surface-base`, one step
              // lighter, with a hairline to give it an edge. The hover tint on top of that is a
              // pointing device, not an affordance — see `RECOMMENDATION_SURFACE`.
              className={`${RECOMMENDATION_SURFACE} grid grid-cols-[1.25rem_1fr] gap-x-2 gap-y-2 px-3 py-3`}
            >
              <span aria-hidden="true" className={`${TYPE.action} text-text-brand-primary`}>
                {recommendation.noActionNeeded ? '○' : '→'}
              </span>

              <p className={`${TYPE.action} ${MEASURE} min-w-0 text-base`}>
                <Field name="recommendations[].businessTitle">
                  <RunProse>{recommendation.businessTitle}</RunProse>
                </Field>
              </p>

              <div className="col-start-2 flex min-w-0 flex-col gap-2">
                <p className={`${TYPE.body} ${MEASURE_WIDE}`}>
                  <Field name="recommendations[].detail">
                    <RunProse>{recommendation.detail}</RunProse>
                  </Field>
                </p>

                {recommendation.steps && recommendation.steps.length > 0 ? (
                  <ol className="flex list-decimal flex-col gap-1 pl-5">
                    {recommendation.steps.map((step, stepIndex) => (
                      <li
                        // biome-ignore lint/suspicious/noArrayIndexKey: an ordered step list from an immutable run
                        key={stepIndex}
                        className={TYPE.body}
                      >
                        <RunProse>{step}</RunProse>
                      </li>
                    ))}
                  </ol>
                ) : null}

                {/* Applies-to and time frame only, under a hairline: values to read off rather
                    than an argument to follow, so they do not blur into the reasoning above. The
                    outcome is gone — `impact` restated what the detail already argues. */}
                <div className="mt-1 border-t border-border-neutral-subtle pt-3">
                  <ActionParameters recommendation={recommendation} />
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <Footnotes type={TYPE} items={footnotes} />
    </div>
  );
};
